import { createContext, createElement, useCallback, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const AuthContext = createContext(null);

const SIGNED_OUT = { authUser: null, claims: {}, loading: false };

/**
 * Read custom claims, forcing one token refresh when member is missing: claims set
 * by Cloud Functions after sign-in only appear in a freshly minted token.
 */
async function readClaims(user, forceRefresh = false) {
  let result = await user.getIdTokenResult(forceRefresh);
  if (!result.claims.member && !forceRefresh) {
    result = await user.getIdTokenResult(true);
  }
  return result.claims;
}

/**
 * Auth state for the whole app: the Firebase user, their claims (member, profileId,
 * admin), and their live users/{profileId} profile.
 */
export function AuthProvider({ children, renderWhileLoading = false }) {
  const [state, setState] = useState({ authUser: null, claims: {}, loading: true });
  const [profile, setProfile] = useState(null);

  useEffect(() => onAuthStateChanged(auth, async (user) => {
    if (!user) {
      setState(SIGNED_OUT);
      return;
    }
    try {
      const claims = await readClaims(user);
      setState({ authUser: user, claims, loading: false });
    } catch (error) {
      console.error('Failed to read auth claims', error);
      setState({ authUser: user, claims: {}, loading: false });
    }
  }), []);

  const profileId = state.claims.member ? state.claims.profileId : null;

  useEffect(() => {
    if (!profileId) {
      setProfile(null);
      return undefined;
    }
    return onSnapshot(
      doc(db, 'users', profileId),
      (snap) => setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      (error) => console.error('Failed to load profile', error)
    );
  }, [profileId]);

  const refresh = useCallback(async () => {
    if (!auth.currentUser) return {};
    const claims = await readClaims(auth.currentUser, true);
    setState({ authUser: auth.currentUser, claims, loading: false });
    return claims;
  }, []);

  const value = {
    authUser: state.authUser,
    // Back-compat alias for components written against the old AuthContext.
    currentUser: state.authUser,
    claims: state.claims,
    profileId,
    profile,
    isMember: Boolean(profileId),
    isAdmin: state.claims.admin === true,
    loading: state.loading,
    refresh,
  };

  return createElement(
    AuthContext.Provider,
    { value },
    state.loading && !renderWhileLoading ? null : children
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
}
